// src/index.js
// Import canonical redirect FIRST to prevent domain split issues
import './canonical-redirect';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { BUILD_ID } from './version';
import './index.css';
import { getApps, getApp } from "firebase/app";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { auth } from './utils/firebase';

// TEMPORARY: Clear service worker and caches to force new bundle load
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(regs => regs.forEach(r => r.unregister()));
  caches && caches.keys && caches.keys().then(keys => keys.forEach(k => caches.delete(k)));
  console.log('[SW] Unregistered and caches cleared');
}

// Smart tripwire to catch legacy HTTP calls (should use unified callables)
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  const _origFetch = window.fetch;
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input.url;
    const isLegacyFn = url?.includes('/startGameLaunch') || url?.includes('/launchPurchasedGame');

    // extract headers/body safely for both (url, init) and Request cases
    const req = input instanceof Request ? input : null;
    const headers = new Headers(
      init?.headers || (req ? req.headers : undefined)
    );

    // body might be on init or on the Request
    let bodyText = '';
    try {
      if (init?.body && typeof init.body === 'string') bodyText = init.body;
      else if (req) bodyText = await req.clone().text();
    } catch { /* ignore */ }

    if (isLegacyFn) {
      const hasAuth = headers.get('Authorization')?.startsWith('Bearer ');
      const hasGmp  = !!headers.get('X-Firebase-GMPID');   // Firebase SDK adds this
      const looksCallable = bodyText.includes('"data":');

      if (hasAuth && looksCallable) {
        console.log('✅ Firebase callable fetch', {
          url,
          auth: 'present',
          gmp: hasGmp,
          bodyLooksCallable: looksCallable
        });
      } else {
        console.warn('🚫 Legacy HTTP call detected - use unified callables instead', {
          url,
          hasAuth,
          hasGmp,
          sampleBody: bodyText.slice(0, 120)
        });
      }
    }

    return _origFetch(input, init);
  };
}

// Router debugging - detect nested routers
if (window.__ROUTER_COUNT__ === undefined) window.__ROUTER_COUNT__ = 0;
window.__ROUTER_COUNT__++;
if (window.__ROUTER_COUNT__ > 1) {
  console.warn("Multiple Routers detected — remove nested <BrowserRouter>.");
}

// Log build version to prove which bundle is running
console.log("APP BUILD", { BUILD_ID, host: window.location.host });

// Wait for the first auth resolution (one-time)
async function waitForInitialAuth() {
  if (typeof auth.authStateReady === "function") {
    // Firebase v10+ API – resolves when initial state is ready
    await auth.authStateReady();
    return;
  }
  // Fallback: resolve on first onAuthStateChanged tick
  await new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, () => { 
      unsub(); 
      resolve(); 
    });
  });
}

// Debug Firebase identity after a short delay to ensure Firebase is initialized
setTimeout(() => {
  console.log("DEBUG: Starting Firebase identity check...");
  (function debugFirebaseIdentity(tag) {
    try {
      console.log("DEBUG: Getting Firebase apps...");
      const apps = getApps();
      console.log("DEBUG: Got apps:", apps.length);
      const app = getApp();
      console.log("DEBUG: Got app:", app.name);
      const auth = getAuth(app);
      console.log("DEBUG: Got auth, currentUser:", !!auth.currentUser);
      console.log(`[FB-ID:${tag}]`, {
        apps: apps.map(a => a.name),
        appName: app.name,
        projectId: app.options?.projectId,
        currentUser: !!auth.currentUser,
      });
    } catch (e) {
      console.log(`[FB-ID:${tag}] error`, e?.message, e);
    }
  })("startup");
}, 100);

// Add global error handler to catch any unhandled errors that might cause logout
window.addEventListener('error', (event) => {
  console.log("GLOBAL ERROR:", event.error?.message, event.error);
});

window.addEventListener('unhandledrejection', (event) => {
  console.log("UNHANDLED PROMISE REJECTION:", event.reason);
});

// Track auth state changes to see when/why user gets signed out
onAuthStateChanged(auth, (user) => {
  console.log("AUTH STATE CHANGE:", {
    timestamp: new Date().toISOString(),
    hasUser: !!user,
    uid: user?.uid,
    email: user?.email,
    emailVerified: user?.emailVerified
  });
});

// Nuke old service workers on custom domain to prevent cache issues
if (window.location.hostname.endsWith("triviaplug.com")) {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations?.().then(rs => {
      rs.forEach(r => {
        console.log("Unregistering old service worker:", r.scope);
        r.unregister();
      });
    });
  }
}

// Block app boot until auth is restored
(async () => {
  console.log("Waiting for initial auth state...");
  await waitForInitialAuth();
  console.log("Auth state ready, mounting app...");
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <ErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  );
})();
